import React, {
  useMemo, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { checkMeiyeAmount } from '$/api/server';
import Button from '$/components/base/Button';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { openInsufficientRadishModal, openVipModal } from '$/store/useVipModalStore';
import { FileTypeEnum } from '$/utils/upload';
import { LoadingType, PathBrush } from '@meitu/whee-infinite-canvas';
import useAIToolsCanvasStore from '@/store/useAIToolsCanvasStore';
import useAIWatermarkStore, {
  setMode,
  setSingleEraserLoading,
  type ManualEraserType,
  setBrushSize,
  setManualEnableEraser,
  setSingleResultImage,
  setSingleResultTaskId,
  updateBatchImage,
  setBatchEraserLoading,
} from '@/store/useAIWatermarkStore';
import {
  type WatermarkParams,
  type VideoWatermarkParams,
  type AiToolTaskResultItem,
} from '@/domains/tools/types';
import { countAiToolCost } from '@/domains/tools/toolsService';
import {
  AI_TOOL_ALGORITHM_URI,
  getWatermarkToolName,
} from '@/domains/tools/aiToolAlgorithmUri';
import { useShallow } from 'zustand/react/shallow';
import { useCommonStore } from '@/store/useCommonStore';
import { replaceTemplateParams } from '@/utils';
import { v4 as uuid } from 'uuid';
import radishIcon from '@/assets/radish.png';
import type useBrushManager from '../../hooks/useBrushManager';
import ModeTab from '../../../ai_eraser/components/ModeTab';
import SelectionBox from '../../../ai_eraser/components/SelectionBox';
import style from './index.module.scss';
import PanelWrapper from '../../../ai_eraser/components/PanelWrapper';
import SliderBox from '../../../ai_eraser/components/SliderBox';
import { getAiToolTaskResult } from '../../../ai_eraser/aiToolsTask';
import { exportMaskUrl } from '../../../ai_eraser/utils';
import {
  AssetToolType,
  autoSaveAiToolGeneratedAsset,
  type AiToolAssetMaterialType,
} from '../../../ai_eraser/utils/assetLibrary';
import {
  buildAiToolVipModalTrackParams,
  getAiToolManualRunType,
  joinAiToolTrackApiNames,
  trackAiToolRunClick,
  trackAiToolRunSuccess,
} from '../../../utils/aiToolUploadTrack';
import { normalizeRequestVideoDuration } from '../../../ai_eraser/utils/mediaCheckAfterUpload';

const MANUAL_ERASER_ITEMS = [
  {
    labelKey: 'swipe',
    value: 'smear' as const,
    icon: 'touchBold',
  },
  {
    labelKey: 'RoboNeo_Web_Box_Select',
    value: 'rect' as const,
    icon: 'squareDashBold',
  },
] as const;

type WatermarkSubmitParam =
  | (WatermarkParams & { task_category: 'watermark'; })
  | (VideoWatermarkParams & { task_category: 'video_watermark'; });

function getRoomIdFromUrl() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('room_id')?.trim() || '';
}

/**
 * 预加载图片结果，让画布切换去水印结果图时尽量命中浏览器缓存。
 */
function preloadWatermarkResultImage(url: string, mediaType: 'image' | 'video') {
  if (typeof window === 'undefined' || !url || mediaType === 'video') return Promise.resolve();
  return new Promise<void>((resolve) => {
    const img = new Image();
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve();
    }, 8000);
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    img.onload = () => {
      if (typeof img.decode === 'function') {
        img.decode().then(finish).catch(finish);
        return;
      }
      finish();
    };
    img.onerror = finish;
    img.src = url;
  });
}

function getBatchItemMediaType(item: { type?: FileTypeEnum; url: string; }) {
  if (item.type === FileTypeEnum.Video) return FileTypeEnum.Video;
  if (item.type === FileTypeEnum.Image) return FileTypeEnum.Image;
  return /\.(mp4|mov|m4v)(\?|#|$)/i.test(item.url) ? FileTypeEnum.Video : FileTypeEnum.Image;
}

/** 自动保存去水印生成结果，图片和视频分别写入资产库对应 tool_type。 */
function autoSaveWatermarkGeneratedAsset(options: {
  duration?: number;
  height?: number;
  mediaType: FileTypeEnum | 'image' | 'video';
  taskId?: string;
  thumbnailUrl?: string;
  url: string;
  width?: number;
}) {
  const isVideo = options.mediaType === FileTypeEnum.Video || options.mediaType === 'video';
  const materialType: AiToolAssetMaterialType = isVideo ? 'video' : 'image';
  autoSaveAiToolGeneratedAsset({
    toolType: isVideo ? AssetToolType.VIDEO_WATERMARK : AssetToolType.IMAGE_WATERMARK,
    sourceScene: 'task_generate',
    materialType,
    url: options.url,
    taskId: options.taskId,
    width: options.width,
    height: options.height,
    duration: options.duration,
    thumbnailUrl: options.thumbnailUrl,
  });
}

/**
 * 根据素材类型返回水印计费用的任务类型。
 */
function getWatermarkCostTaskCategory(mediaType?: FileTypeEnum) {
  return mediaType === FileTypeEnum.Video ? 'video_watermark' : 'watermark';
}

/**
 * 按素材类型构建新版 submit item，视频必须走 video_watermark；自动识别统一传 scenes=1，不再附带旧版 target/automatic。
 */
function buildAutoWatermarkSubmitParam(
  initImage: string,
  mediaType: FileTypeEnum,
  videoDuration?: number,
): WatermarkSubmitParam {
  if (mediaType === FileTypeEnum.Video) {
    return {
      task_category: 'video_watermark',
      init_image: initImage,
      scenes: 1,
      video_duration: normalizeRequestVideoDuration(videoDuration),
    };
  }
  return {
    task_category: 'watermark',
    init_image: initImage,
    scenes: 1,
  };
}

/**
 * 构建图片手动去水印 submit item，按文档通过 watermark + target=others 传自定义 mask，手动场景传 scenes=2。
 */
function buildManualWatermarkSubmitParam(initImage: string, maskImage: string): WatermarkSubmitParam {
  return {
    task_category: 'watermark',
    init_image: initImage,
    scenes: 2,
    target: 'others',
    automatic: true,
    mask_image: maskImage,
  };
}

/**
 * 构建 AI 去水印计费项；params 内带 task_category 供服务端分发计费。
 */
function buildCostItems(
  paramsMap: Record<string, Record<string, any>>,
  options: Record<string, { toolName?: string; mediaType?: FileTypeEnum; videoDuration?: number; }> = {},
) {
  return Object.entries(paramsMap).map(([id, params]) => {
    const mediaType = options[id]?.mediaType || FileTypeEnum.Image;
    const videoDuration = options[id]?.videoDuration;
    return {
      id,
      tool_name: options[id]?.toolName || getWatermarkToolName(options[id]?.mediaType),
      size: '',
      quality: '',
      resolution: '',
      params: {
        task_category: 'task_category' in params
          ? params.task_category
          : getWatermarkCostTaskCategory(options[id]?.mediaType),
        ...params,
        ...(mediaType === FileTypeEnum.Video ? { video_duration: normalizeRequestVideoDuration(videoDuration) } : {}),
        media_type: mediaType,
      },
    };
  });
}

/**
 * 查询 AI 去水印消耗。
 */
async function fetchAIWatermarkCost(
  paramsMap: Record<string, Record<string, any>>,
  options?: Record<string, { toolName?: string; mediaType?: FileTypeEnum; videoDuration?: number; }>,
) {
  const result = await countAiToolCost({
    room_id: getRoomIdFromUrl(),
    items: buildCostItems(paramsMap, options),
  });
  return (result.items || []).reduce((sum, item) => sum + Number(item.cost || 0), 0);
}

/**
 * 提取本次去水印 countcost 使用的 tool_name，作为订阅弹窗埋点 api_name。
 */
function getWatermarkCostApiName(
  paramsMap: Record<string, Record<string, any>>,
  options?: Record<string, { toolName?: string; mediaType?: FileTypeEnum; videoDuration?: number; }>,
) {
  return joinAiToolTrackApiNames(
    buildCostItems(paramsMap, options).map((item) => item.tool_name),
  );
}

/**
 * 构建单素材自动去水印计价参数，视频素材需要把时长放入 params.video_duration。
 */
function buildSingleAutoWatermarkCostParams(initImage: string, mediaType: FileTypeEnum, videoDuration?: number) {
  const id = uuid();
  return {
    params: {
      [id]: buildAutoWatermarkSubmitParam(initImage, mediaType, videoDuration),
    },
    options: {
      [id]: { toolName: getWatermarkToolName(mediaType), mediaType, videoDuration },
    },
  };
}

function buildSingleManualWatermarkCostParams(initImage: string) {
  const id = uuid();
  return {
    params: {
      [id]: {
        init_image: initImage,
        mask_image: '',
      },
    },
    options: {
      [id]: {
        toolName: AI_TOOL_ALGORITHM_URI.ERASER_TEXT_REMOVER,
        mediaType: FileTypeEnum.Image,
      },
    },
  };
}

/**
 * 构建批量去水印计价参数，按每个素材的媒体类型与时长独立计价。
 */
function buildBatchWatermarkCostParams(
  list: Array<{ id: string; url: string; type?: FileTypeEnum; duration?: number; requestDuration?: number; }>,
) {
  const params = list.reduce<Record<string, Record<string, any>>>(
    (acc, item) => ({
      ...acc,
      [item.id]: buildAutoWatermarkSubmitParam(item.url, getBatchItemMediaType(item), item.requestDuration),
    }),
    {},
  );
  const options = list.reduce<Record<string, { toolName?: string; mediaType?: FileTypeEnum; videoDuration?: number; }>>(
    (acc, item) => ({
      ...acc,
      [item.id]: {
        toolName: getWatermarkToolName(getBatchItemMediaType(item)),
        mediaType: getBatchItemMediaType(item),
        videoDuration: item.requestDuration,
      },
    }),
    {},
  );
  return { params, options };
}

export type BrushManager = ReturnType<typeof useBrushManager>;

interface WatermarkLeftPanelProps {
  brushManager: BrushManager;
  uploading?: boolean;
}

export default function WatermarkLeftPanel({ brushManager, uploading = false }: WatermarkLeftPanelProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const processingFailedText = languageStore.smartcatTranslationText((v) => v.processing_failed);
  const errorCodeToastText = languageStore.smartcatTranslationText((v) => v.Error_code_toast);
  const unlockBatchModeText = languageStore.smartcatTranslationText((v) => v.activate_membership_to_unlock_batch_mode_);
  const {
    setBrushMode, clearMask, currentMode,
  } = brushManager;
  const { hasMask } = useAIToolsCanvasStore(useShallow((state) => ({
    hasMask: state.hasMask,
  })));
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const toLogin = useOfficialStore((state) => state.toLogin);
  const isVip = useOfficialStore((state) => Boolean(state.userInfo.subscribe?.isValid));

  const { render, imageTarget, historyPlugins } = useAIToolsCanvasStore(useShallow((state) => ({
    render: state.render,
    imageTarget: state.imageTarget,
    historyPlugins: state.historyPlugins,
  })));
  const {
    mode,
    singleEraserLoading,
    brushSize,
    manualEnableEraser,
    singleOriginalImage,
    singleResultImage,
    singleMediaType,
    singleVideoDuration,
    singleVideoRequestDuration,
    batchImages,
    batchEraserLoading,
  } = useAIWatermarkStore(
    useShallow((state) => ({
      mode: state.mode,
      singleOriginalImage: state.singleOriginalImage,
      singleResultImage: state.singleResultImage,
      brushSize: state.brushSize,
      manualEnableEraser: state.manualEnableEraser,
      singleEraserLoading: state.singleEraserLoading,
      singleMediaType: state.singleMediaType,
      singleVideoDuration: state.singleVideoDuration,
      singleVideoRequestDuration: state.singleVideoRequestDuration,
      batchImages: state.batchImages,
      batchEraserLoading: state.batchEraserLoading,
    })),
  );
  const [singleWatermarkCost, setSingleWatermarkCost] = useState<number | null>(null);
  const [batchWatermarkCost, setBatchWatermarkCost] = useState<number | null>(null);

  const modeTabs = useMemo(
    () => [
      { key: 'single' as const, label: languageStore.smartcatTranslationText((v) => v.single_sheet_mode) },
      { key: 'batch' as const, label: languageStore.smartcatTranslationText((v) => v.batch), showVipBadge: true },
    ],
    [languageStore],
  );
  const batchCostKey = useMemo(
    () => batchImages.map((item) => `${item.id}:${item.url}:${item.type || ''}:${item.duration || ''}:${item.requestDuration || ''}`).join('|'),
    [batchImages],
  );

  useEffect(() => {
    const initImage = singleResultImage || singleOriginalImage;
    if (!isUserLoggedIn || !initImage) {
      setSingleWatermarkCost(null);
      return undefined;
    }

    const mediaType = singleMediaType === 'video' ? FileTypeEnum.Video : FileTypeEnum.Image;
    const { params, options } = singleMediaType === 'video'
      ? buildSingleAutoWatermarkCostParams(initImage, mediaType, singleVideoRequestDuration)
      : buildSingleManualWatermarkCostParams(initImage);

    let cancelled = false;
    fetchAIWatermarkCost(params, options)
      .then((cost) => {
        if (!cancelled) setSingleWatermarkCost(cost);
      })
      .catch(() => {
        if (!cancelled) setSingleWatermarkCost(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isUserLoggedIn, singleMediaType, singleOriginalImage, singleResultImage, singleVideoRequestDuration]);

  useEffect(() => {
    if (!isUserLoggedIn || !isVip || !batchImages.length) {
      setBatchWatermarkCost(null);
      return undefined;
    }

    const list = useAIWatermarkStore.getState().batchImages;
    const { params, options } = buildBatchWatermarkCostParams(list);
    let cancelled = false;
    fetchAIWatermarkCost(params, options)
      .then((cost) => {
        if (!cancelled) setBatchWatermarkCost(cost);
      })
      .catch(() => {
        if (!cancelled) setBatchWatermarkCost(null);
      });

    return () => {
      cancelled = true;
    };
  }, [batchCostKey, batchImages.length, isUserLoggedIn, isVip]);

  const handleManualEraserClick = (value: ManualEraserType) => {
    setBrushMode(value);
  };

  /**
   * 校验 AI 去水印余额，不足时带单工具来源参数打开订阅弹窗。
   */
  const ensureRadishEnough = async (cost: number, apiName?: string) => {
    const data = await checkMeiyeAmount();
    if (Number(data.amount || 0) < cost) {
      message.error('RoboNeo_Web_studio_changing_toast', 3);
      openInsufficientRadishModal?.('func_token_short', undefined, buildAiToolVipModalTrackParams('remove_aimark', {
        entrance: 'insufficient_balance',
        apiName,
      }));
      return false;
    }
    return true;
  };

  /**
   * 非 VIP 使用批量模式时，带执行页来源参数打开订阅弹窗。
   */
  const openBatchVipModal = () => {
    try {
      if (!openVipModal) {
        message.error(unlockBatchModeText);
        return false;
      }
      openVipModal('func_runpage', undefined, buildAiToolVipModalTrackParams('remove_aimark'));
      return true;
    } catch (error) {
      console.error('打开会员弹窗失败:', error);
      message.error(unlockBatchModeText);
      return false;
    }
  };

  const renderCost = (cost: number | null) => {
    if (cost === null) return null;
    return (
      <span className={style.button_cost}>
        <img src={radishIcon.src} alt="" className={style.radish_icon} />
        <span>{cost}</span>
      </span>
    );
  };

  const prevUploadedImage = useRef(singleOriginalImage);

  // 只要上传完毕并在画布加载了新图，就自动去一次水印
  useEffect(() => {
    if (mode === 'single' && singleOriginalImage && imageTarget && !uploading) {
      if (singleOriginalImage !== prevUploadedImage.current) {
        prevUploadedImage.current = singleOriginalImage;
        handleSubmit('single', true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, singleOriginalImage, imageTarget, uploading]);

  const handleSubmit = async (type: 'single' | 'batch', isAuto = false) => {
    if (!isUserLoggedIn) {
      await toLogin().then(() => {
        initUserInfo();
      });
      if (type === 'single') {
        handleStartSingleWatermark(isAuto);
      } else {
        handleStartBatchWatermark();
      }
      return;
    }
    if (type === 'single') {
      handleStartSingleWatermark(isAuto);
    } else {
      handleStartBatchWatermark();
    }
  };

  /**
   * 单图模式去水印
   * - 图片 + 有 mask：手动模式 scenes=2，target='others'，传 mask_image
   * - 图片上传后自动处理 / 视频：自动模式 scenes=1
   */
  const handleStartSingleWatermark = async (isAuto = false) => {
    if (singleEraserLoading) return;
    setSingleEraserLoading(true);
    let keepCanvasLoadingUntilResultSwap = false;

    const reportFailure = (toastContent?: string) => {
      const text = toastContent?.trim();
      message.error(text || errorCodeToastText);
    };

    try {
      const id = uuid();
      const init_image = isAuto ? singleOriginalImage : (singleResultImage || singleOriginalImage);
      const mediaType = singleMediaType === 'video' ? FileTypeEnum.Video : FileTypeEnum.Image;
      const costConfig = (singleMediaType === 'video' || isAuto)
        ? buildSingleAutoWatermarkCostParams(init_image, mediaType, singleVideoRequestDuration)
        : buildSingleManualWatermarkCostParams(init_image);
      const latestCost = await fetchAIWatermarkCost(costConfig.params, costConfig.options);
      if ((!isAuto || singleMediaType === 'video') && singleWatermarkCost !== null && latestCost !== singleWatermarkCost) {
        message.error(errorCodeToastText);
        return;
      }
      const radishEnough = await ensureRadishEnough(
        latestCost,
        getWatermarkCostApiName(costConfig.params, costConfig.options),
      );
      if (!radishEnough) return;

      if (singleMediaType === 'video' || isAuto) {
        // 视频模式或自动处理模式：全自动去水印
        render?.Actions.setLoading({
          id: imageTarget?._id_ || '',
          type: LoadingType.CIRCLE_DANCE,
          text: ' ',
          blur: true,
          maskOpacity: 0.2,
        });
        const result = await getAiToolTaskResult({
          params: {
            [id]: buildAutoWatermarkSubmitParam(init_image, mediaType, singleVideoRequestDuration),
          },
          onTaskCreated: (ids) => trackAiToolRunClick({
            func: 'remove_aimark',
            taskIds: Object.values(ids),
            generateType: 1,
          }),
        });
        if (result.success) {
          const out = result.data.result?.[id][0];
          const url = out?.url_sign || out?.url_short || '';
          if (url) {
            trackAiToolRunSuccess({
              func: 'remove_aimark',
              taskId: result.ids?.[id],
              resultUrl: url,
              contentType: singleMediaType === 'video' ? 'video' : 'image',
              generateType: 1,
            });
            autoSaveWatermarkGeneratedAsset({
              mediaType: singleMediaType,
              url,
              taskId: result.ids?.[id],
              width: out?.width,
              height: out?.height,
              duration: out?.duration || singleVideoDuration,
              thumbnailUrl: out?.url_thumb_sign,
            });
          }
          preloadWatermarkResultImage(url, singleMediaType).catch(() => {});
          keepCanvasLoadingUntilResultSwap = Boolean(url) && url !== init_image;
          setSingleResultImage(url);
          setSingleResultTaskId(result.ids?.[id] || '');
          checkMeiyeAmount().catch(() => {});
        } else {
          reportFailure(result.message || undefined);
        }
      } else {
        // 图片模式：手动消除，使用 mask
        const maskUrl = await exportMaskUrl();
        if (!maskUrl) {
          reportFailure();
          console.error('任务失败: mask 导出失败');
          setSingleEraserLoading(false);
          return;
        }
        const maskContainer = (render?._FC.freeDrawingBrush as PathBrush | undefined)?.maskContainer;
        if (maskContainer) {
          maskContainer.set('visible', false);
          render?._FC.renderAll();
        }
        render?.Actions.setLoading({
          id: imageTarget?._id_ || '',
          type: LoadingType.CIRCLE_DANCE,
          text: ' ',
          blur: true,
          maskOpacity: 0.2,
        });

        const result = await getAiToolTaskResult({
          params: {
            [id]: buildManualWatermarkSubmitParam(init_image, maskUrl),
          },
          onTaskCreated: (ids) => trackAiToolRunClick({
            func: 'remove_aimark',
            taskIds: Object.values(ids),
            generateType: 1,
            manualType: getAiToolManualRunType(currentMode),
            manualLineSize: getAiToolManualRunType(currentMode) === 'daub' ? brushSize : undefined,
          }),
        });
        if (result.success) {
          const out = result.data.result?.[id][0];
          const url = out?.url_sign || out?.url_short || '';
          if (url) {
            trackAiToolRunSuccess({
              func: 'remove_aimark',
              taskId: result.ids?.[id],
              resultUrl: url,
              contentType: 'image',
              generateType: 1,
              manualType: getAiToolManualRunType(currentMode),
              manualLineSize: getAiToolManualRunType(currentMode) === 'daub' ? brushSize : undefined,
            });
            autoSaveWatermarkGeneratedAsset({
              mediaType: 'image',
              url,
              taskId: result.ids?.[id],
              width: out?.width,
              height: out?.height,
              duration: out?.duration,
              thumbnailUrl: out?.url_thumb_sign,
            });
          }
          preloadWatermarkResultImage(url, 'image').catch(() => {});
          keepCanvasLoadingUntilResultSwap = Boolean(url) && url !== init_image;
          setSingleResultImage(url);
          setSingleResultTaskId(result.ids?.[id] || '');
          clearMask();
          historyPlugins?.history.clear();
          checkMeiyeAmount().catch(() => {});
        } else {
          reportFailure(result.message || undefined);
        }
      }
    } catch (error) {
      console.error('error', error);
      reportFailure();
    } finally {
      if (!keepCanvasLoadingUntilResultSwap) {
        render?.Actions.setLoaded(imageTarget?._id_ || '');
        setSingleEraserLoading(false);
      }
      // 恢复 mask 层显示
      const containerToRestore = (render?._FC.freeDrawingBrush as PathBrush | undefined)?.maskContainer;
      if (containerToRestore) {
        containerToRestore.set('visible', true);
        render?._FC.renderAll();
      }
    }
  };

  /**
   * 批量模式去水印
   * @returns
   */
  const handleStartBatchWatermark = async () => {
    if (batchEraserLoading) return;
    if (!useOfficialStore.getState().userInfo.subscribe?.isValid) {
      openBatchVipModal();
      return;
    }
    if (!batchImages.length) return;

    const list = useAIWatermarkStore.getState().batchImages;
    try {
      const { params, options } = buildBatchWatermarkCostParams(list);
      const latestCost = await fetchAIWatermarkCost(params, options);
      if (batchWatermarkCost !== null && latestCost !== batchWatermarkCost) {
        message.error(errorCodeToastText);
        return;
      }
      const radishEnough = await ensureRadishEnough(latestCost, getWatermarkCostApiName(params, options));
      if (!radishEnough) return;
    } catch (error) {
      console.error('批量去水印价格校验失败:', error);
      message.error(errorCodeToastText);
      return;
    }

    list.forEach((item) => updateBatchImage(item.id, { status: 'processing', progress: 0 }));
    setBatchEraserLoading(true);

    const onTaskUpdate = (frontendId: string, item: AiToolTaskResultItem) => {
      if (item.status === 100) {
        const out = item.result_images?.[0];
        const url = out?.url_sign || out?.url_short || '';
        const batchItem = list.find((i) => i.id === frontendId);
        const mediaType = batchItem ? getBatchItemMediaType(batchItem) : FileTypeEnum.Image;
        if (url) {
          trackAiToolRunSuccess({
            func: 'remove_aimark',
            taskId: item.id,
            resultUrl: url,
            contentType: mediaType === FileTypeEnum.Video ? 'video' : 'image',
            generateType: 2,
          });
          autoSaveWatermarkGeneratedAsset({
            mediaType,
            url,
            taskId: item.id,
            width: out?.width,
            height: out?.height,
            duration: out?.duration || batchItem?.duration,
            thumbnailUrl: out?.url_thumb_sign,
          });
        }
        updateBatchImage(frontendId, {
          status: 'success',
          taskId: item.id,
          resultUrl: url,
          resultImageLoading: true,
          width: out?.width,
          height: out?.height,
        });
      } else if (item.status >= 90 && item.status < 100) {
        updateBatchImage(frontendId, {
          status: 'failed',
          taskId: item.id,
          errorText: item.fail_msg || processingFailedText,
        });
      }
    };

    try {
      const payload = {
        params: list.reduce<Record<string, WatermarkSubmitParam>>(
          (acc, item) => ({
            ...acc,
            [item.id]: buildAutoWatermarkSubmitParam(item.url, getBatchItemMediaType(item), item.requestDuration),
          }),
          {},
        ),
      };

      const result = await getAiToolTaskResult({
        ...payload,
        onTaskUpdate,
        onTaskCreated: (ids) => trackAiToolRunClick({
          func: 'remove_aimark',
          taskIds: Object.values(ids),
          generateType: 2,
        }),
      });
      if (!result.success) {
        const failMsg = result.message || errorCodeToastText;
        message.error(errorCodeToastText);
        const current = useAIWatermarkStore.getState().batchImages;
        const hasAnyTerminal = current.some(
          (i) => i.status === 'success' || i.status === 'failed' || i.status === 'unrecognized',
        );
        if (!hasAnyTerminal) {
          list.forEach((item) => updateBatchImage(item.id, { status: 'failed', errorText: failMsg }));
        }
      } else {
        checkMeiyeAmount().catch(() => {});
      }
    } catch (error) {
      console.error('批量去水印失败:', error);
      message.error(errorCodeToastText);
      const listCurrent = useAIWatermarkStore.getState().batchImages;
      listCurrent.forEach((item) => updateBatchImage(item.id, { status: 'failed', errorText: '请求异常' }));
    } finally {
      setBatchEraserLoading(false);
    }
  };

  // 单张图片模式：有 mask 才可点击
  // 单张视频模式：直接可点击
  const singleEraserBtnDisabled = useMemo(() => {
    if (uploading || singleEraserLoading) return true;
    if (singleMediaType === 'video') return false;
    return !hasMask;
  }, [uploading, singleEraserLoading, hasMask, singleMediaType]);

  // 是否为单张图片模式（展示涂抹工具）
  const showManualTools = mode === 'single' && singleMediaType === 'image';
  const batchModeLocked = !isVip;
  const renderBatchButtonContent = () => {
    if (batchEraserLoading) return `${languageStore.smartcatTranslationText((v) => v.processing)}...`;
    if (batchModeLocked) return unlockBatchModeText;
    return (
      <span className={style.button_content}>
        <span>
          {replaceTemplateParams(
            batchImages.some((item) => getBatchItemMediaType(item) === FileTypeEnum.Video)
              ? languageStore.smartcatTranslationText((v) => v.batch_start_removal_30_units_)
              : languageStore.smartcatTranslationText((v) => v.batch_removal_initiated_30_sheets_),
            { N: batchImages.length },
          )}
        </span>
        {renderCost(batchWatermarkCost)}
      </span>
    );
  };

  return createPortal(
    <div className={style.left_panel}>
      {/* 模式切换tab */}
      <ModeTab activeMode={mode} tabs={modeTabs} onChange={setMode} />

      {mode === 'single' ? (
        // 单图模式
        <>
          <div className={style.inner_scroll_box}>
            {/* 手动消除：仅图片模式展示 */}
            {showManualTools && (
              <PanelWrapper
                title={languageStore.smartcatTranslationText((v) => v.manual)}
                subtitle={languageStore.smartcatTranslationText((v) => v.manually_eliminate_selected_areas)}
                contentClassName={style.panel_wrapper_content}
              >
                {MANUAL_ERASER_ITEMS.map((item) => (
                  <SelectionBox
                    key={item.value}
                    value={item.value}
                    label={languageStore.smartcatTranslationText((v) => v[item.labelKey])}
                    icon={item.icon}
                    active={currentMode === item.value}
                    onClick={() => handleManualEraserClick(item.value)}
                  />
                ))}

                {/* 切换橡皮擦和滑块 */}
                <SliderBox
                  enable={manualEnableEraser}
                  value={brushSize}
                  onChange={(val: number) => {
                    setBrushSize(val);
                  }}
                  toggleEnable={(v: boolean) => {
                    setManualEnableEraser(v);
                  }}
                  onClear={() => {
                    clearMask();
                  }}
                />
              </PanelWrapper>
            )}
          </div>
          <Button
            type="primary"
            disabled={singleEraserBtnDisabled}
            className={style.start_eraser_btn}
            onClick={() => handleSubmit('single')}
          >
            {singleEraserLoading
              ? `${languageStore.smartcatTranslationText((v) => v.processing)}...`
              : (
                <span className={style.button_content}>
                  <span>{languageStore.smartcatTranslationText((v) => v.remove)}</span>
                  {renderCost(singleWatermarkCost)}
                </span>
              )}
          </Button>
        </>
      ) : (
        // 批量模式：无参数面板
        <>
          <div className={style.inner_scroll_box} />
          <Button
            type="primary"
            disabled={batchModeLocked ? (uploading || batchEraserLoading) : (
              uploading || batchEraserLoading || !batchImages.length
            )}
            className={classNames(style.start_eraser_btn, {
              [style.unlock_batch_btn]: batchModeLocked,
            })}
            onClick={() => handleSubmit('batch')}
          >
            {renderBatchButtonContent()}
          </Button>
        </>
      )}
    </div>,
    document.body,
  );
}
